import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Typography } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import LoaderButton from './LoaderButton';
import { getCurrencySymbol } from '../helpers/ticket';

const styles = {
    root: {
        marginTop: '20px',
        minHeight: '100px',
        marginBottom: '20px',
        padding: '0 10px 10px',
        textAlign: 'center'
    },
    input: {
        width: '100%',
        maxWidth: '250px',
        display: 'inline-block',
        margin: '15px auto 25px',
        verticalAlign: 'unset',
        '& > div': {
            display: 'block'
        },
        '& input': {
            maxWidth: '220px'
        }
    },
    discountApplied: {
        marginTop: '-10px',
        marginBottom: '5px',
        color: 'rgba(44, 136, 0, 0.87)'
    },
    button: {
        margin: '15px 0 25px 10px',
        height: '56px',
        verticalAlign: 'top'
    },
    error: {
        marginTop: '-10px',
        marginBottom: '5px',
        color: red[500]
    },
    warning: {
        backgroundColor: red[500],
        color: '#fff',
        border: '1px solid #ff0000'
    }
};

class VoucherForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            processing: false,
            error: '',
            warning: '',
            voucherValue: 0.0,
            voucherId: '',
            applied: false
        };
    }

    handleChange = event => {
        this.setState({
            voucherId: event.target.value
        });
    };

    handleApply = async () => {
        this.setState({
            processing: true,
            error: '',
            warning: '',
            applied: false,
            voucherValue: 0.0
        });

        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const { voucherId } = this.state;
        const { client, setVoucher } = this.props;

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/voucher/apply/${
                    client.id
                }/${voucherId.trim()}`,
                null,
                {
                    headers
                }
            );

            if (response.data.id) {
                const stateUpdate = {
                    processing: false,
                    applied: true,
                    voucherValue: response.data.value,
                    voucherCurrency: response.data.currency || 'gbp'
                };
                if (response.data.status === 'pending') {
                    stateUpdate.warning = (
                        <>
                            <strong>WARNING: </strong>
                            This voucher code is currently being applied to an
                            outstanding order. Re-applying it at this time may
                            result in payment being taken but your order
                            failing.
                        </>
                    );
                }
                this.setState(stateUpdate);

                setVoucher(response.data);
            } else {
                this.setState({
                    error: 'Invalid voucher code.',
                    processing: false,
                    applied: false
                });
                setVoucher(null);
            }
        } catch (e) {
            console.error(e);
            this.setState({
                error: 'Invalid voucher code.',
                processing: false,
                applied: false
            });
            setVoucher(null);
        }
    };

    render() {
        const { allowEdit, classes, message } = this.props;
        const {
            voucherId,
            processing,
            error,
            warning,
            applied,
            voucherValue,
            voucherCurrency
        } = this.state;

        const enableTextField = allowEdit && !applied;

        return (
            <div className={classes.root}>
                <TextField
                    className={classes.input}
                    id="voucherId"
                    type="text"
                    label="Voucher code"
                    value={voucherId}
                    variant="outlined"
                    onChange={this.handleChange}
                    disabled={!enableTextField}
                />
                <LoaderButton
                    className={classes.button}
                    variant="outlined"
                    color="primary"
                    type="submit"
                    id="voucherApply"
                    isLoading={processing}
                    text={applied ? '\u2705' : 'Apply'}
                    loadingText="Applying..."
                    onClick={this.handleApply}
                    disabled={!enableTextField}
                />
                {!error && applied && (
                    <>
                        <Typography
                            className={classes.discountApplied}
                            variant="body2"
                        >
                            {getCurrencySymbol(voucherCurrency)}
                            {voucherValue} voucher code applied!
                        </Typography>
                        {message}
                    </>
                )}
                {error && (
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                )}
                {warning && (
                    <Typography className={classes.warning} variant="body2">
                        {warning}
                    </Typography>
                )}
            </div>
        );
    }
}

VoucherForm.propTypes = {
    allowEdit: PropTypes.bool,
    classes: PropTypes.shape().isRequired,
    client: PropTypes.shape().isRequired,
    setVoucher: PropTypes.func.isRequired,
    message: PropTypes.node
};

VoucherForm.defaultProps = {
    allowEdit: true,
    message: ''
};

export default withStyles(styles)(VoucherForm);
